import axios from 'axios';
import { auth } from '../firebase';
import { toast } from 'react-toastify';
import { createBrowserHistory } from 'history';
import { getDeviceHeaders } from './deviceInfo';
import { waitForCoreSeqJob } from './coreSeqJobWait';
import { interceptLegacy } from '../offline/runtime';
import { traceLogout } from './logoutTrace';
import { captureSentryApiFailure } from '../config/sentry';

function generateRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export const BASEURL =
  'https://api.usenode.com/';

export const EMULURL = 'http://127.0.0.1:5001/node-canada/northamerica-northeast1/';

// --- Circuit breaker ---
const CIRCUIT_BREAKER_THRESHOLD = 5;
const CIRCUIT_BREAKER_WINDOW_MS = 60 * 1000;
const CIRCUIT_BREAKER_COOLDOWN_MS = 60 * 1000;

let circuitFailures = [];
let circuitOpenUntil = 0;

function recordCircuitFailure() {
  const now = Date.now();
  circuitFailures.push(now);
  circuitFailures = circuitFailures.filter(
    (t) => t > now - CIRCUIT_BREAKER_WINDOW_MS
  );
  if (circuitFailures.length >= CIRCUIT_BREAKER_THRESHOLD) {
    circuitOpenUntil = now + CIRCUIT_BREAKER_COOLDOWN_MS;
    circuitFailures = [];
  }
}

function recordCircuitSuccess() {
  circuitFailures = [];
}

export function isCircuitOpen() {
  return Date.now() < circuitOpenUntil;
}

const RETRYABLE_STATUSES = new Set([429, 502, 503, 504]);
const MAX_RETRIES = 2;

// Sandbox mode routing: when active, element list/detail/CRUD operations are
// transparently redirected to the isolated `sandbox` Cloud Function. Payloads
// and response shapes are compatible, so call sites stay unchanged.
const SANDBOX_REMAP = {
  'POST bosV4/list': 'sandbox/list',
  'POST bosV4/single': 'sandbox/single',
  'POST bosV4/singleDoc': 'sandbox/singleDoc',
  'POST bosV4/blocks': 'sandbox/blocks',
  'POST bosV4/movements': 'sandbox/movements',
  'POST bosV4/movements/adjust': 'sandbox/movements/adjust',
  'POST coreSeqV5': 'sandbox/',
  'POST coreSeqV6': 'sandbox/',
  'POST coreSeqV6/single': 'sandbox/',
  'PATCH coreSeqV6/single': 'sandbox/',
  'DELETE coreSeqV6': 'sandbox/',
};

export function isSandboxModeActive() {
  try {
    return localStorage.getItem('sandboxMode') === '1';
  } catch (_) {
    return false;
  }
}

function shouldRetry(error, attempt) {
  if (attempt >= MAX_RETRIES) return false;
  if (!error?.response?.status) return false;
  return RETRYABLE_STATUSES.has(error.response.status);
}

function getRetryDelay(error, attempt) {
  const retryAfter = error?.response?.headers?.['retry-after'];
  if (retryAfter) {
    const seconds = parseInt(retryAfter, 10);
    if (!isNaN(seconds) && seconds > 0 && seconds <= 120) {
      return seconds * 1000;
    }
  }
  const base = Math.min(1000 * Math.pow(2, attempt), 10000);
  const jitter = Math.random() * base * 0.3;
  return base + jitter;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getBusinessToken() {
  try {
    return localStorage.getItem('businessToken');
  } catch (_) {
    return null;
  }
}

export function deviceTokenUserForRequest(url, noAuth, userId) {
  if (noAuth || !userId) return undefined;
  try {
    return new URL(url, BASEURL).origin === new URL(BASEURL).origin ? userId : undefined;
  } catch (_) { return undefined; }
}

// Mint a fresh business token for the current business using the Firebase
// identity. Used to transparently recover from an expired/missing token
// without forcing the user back to login.
function businessTokenContext() {
  return {
    user: auth.currentUser,
    businessId: localStorage.getItem('businessId'),
    sandboxMode: isSandboxModeActive(),
  };
}

function isCurrentBusinessTokenContext(context) {
  return context.user === auth.currentUser &&
    context.businessId === localStorage.getItem('businessId') &&
    context.sandboxMode === isSandboxModeActive();
}

function notifySessionExpired(error, context) {
  const code = error?.response?.data?.code || error?.response?.data?.error?.code;
  if (code === 'SESSION_EXPIRED' && isCurrentBusinessTokenContext(context)) {
    window.dispatchEvent(new CustomEvent('node:session-expired', {
      detail: { userId: context.user?.uid, businessId: context.businessId },
    }));
  }
}

async function mintBusinessToken(context, signal) {
  try {
    const { user, businessId } = context;
    if (!user || !businessId || signal?.aborted || !isCurrentBusinessTokenContext(context)) return null;
    const idToken = await user.getIdToken();
    if (signal?.aborted || !isCurrentBusinessTokenContext(context)) return null;
    const response = await axios({
      method: 'POST',
      url: `${BASEURL}users/business-token`,
      data: { businessId },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
        ...getDeviceHeaders(user.uid),
      },
      timeout: 15000,
      ...(signal && { signal }),
    });
    const token = response?.data?.access?.token;
    if (token && !signal?.aborted && isCurrentBusinessTokenContext(context)) {
      localStorage.setItem('businessToken', token);
      return token;
    }
    return null;
  } catch (error) {
    notifySessionExpired(error, context);
    return null;
  }
}

function isBusinessTokenExpired(error) {
  const response = error?.response;
  return !!response?.data?.intCode?.endsWith('TKEXP001') ||
    (response?.status === 401 && response?.data?.error?.code === 'auth.business_token_expired');
}

const BUSINESS_TOKEN_OPTIONAL_ROUTES = new Set([
  'users/session',
  'private/learningWorkshop',
  'users/password',
  'users/register',
  'users/sendVerificationCode',
  'users/verifyCode',
  'users/device-security',
  'users/verify-device',
]);

function isBusinessTokenOptional(url) {
  if (!url) return false;
  const normalized = url
    .replace(/^https?:\/\/[^/]+\//, '')
    .replace(/^\//, '')
    .split('?')[0];
  return BUSINESS_TOKEN_OPTIONAL_ROUTES.has(normalized);
}

function redirectSigninFirebase(reason, intCode) {
  const history = createBrowserHistory();
  traceLogout(reason, { intCode: intCode || null });
  auth.signOut();
  localStorage.removeItem('businessToken');
  history.push('/');
}

const nodeAxiosFirebase = async ({
  method = 'POST',
  url,
  body,
  noAuth: noAuthParam,
  errorToast = false,
  showLoading = false,
  headers,
  options,
  t,
  lang = localStorage.getItem('i18nextLng') || 'fr',
  signal,
  retry = true,
  _isAuthRetry = false,
  expectedUserId,
}) => {
  const tokenContext = businessTokenContext();
  if (!isSandboxModeActive()) {
    try {
      const local = await interceptLegacy((method || 'POST').toUpperCase(), url, body);
      if (local) return local;
    } catch (error) {
      return { errorData: { message: error.message, status: 422, offline: true } };
    }
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { errorData: { message: 'Disponible en ligne seulement. Ouvrez votre espace hors ligne.', status: 503, offline: true } };
  }
  const noAuth = noAuthParam ?? (url && url.includes('public.usenode.com') && !url.includes('/render-pdf'));
  let toastId = null;
  const currentPath = window.location.pathname;

  // Sandbox mode: redirect supported element ops to the sandbox backend, and
  // block unsupported element mutations so they never hit production.
  let effectiveUrl = url;
  if (url && !url.startsWith('http') && isSandboxModeActive()) {
    const m = (method || 'POST').toUpperCase();
    const inventoryLifecycle = body?.addonId === 'inventory' && /^addons\/(get|install|updateConfig|activate|disable|uninstall|validateDependencies)$/.test(url);
    const inventoryAction = url === 'addons/executeAction' && String(body?.actionKey || '').startsWith('inventory.');
    const remap = inventoryLifecycle ? `sandbox/inventory/lifecycle/${url.split('/')[1]}`
      : inventoryAction ? 'sandbox/inventory/commands/execute'
      : url.startsWith('addons/inventory/')
      ? url.replace(/^addons\//, 'sandbox/')
      : SANDBOX_REMAP[`${m} ${url}`];
    if (inventoryAction) body = { command: body.actionKey, target: body.params?.target || {
      structureId: body.context?.structureId, documentPath: body.context?.documentPath || body.context?.elementPath,
    }, payload: body.params?.payload || {}, operationId: body.params?.operationId, expectedVersion: body.params?.expectedVersion };
    if (remap) {
      effectiveUrl = remap;
    } else if (
      (url === 'coreSeqV5' ||
        url.startsWith('coreSeqV5/') ||
        url === 'coreSeqV6' ||
        url.startsWith('coreSeqV6/')) &&
      (m === 'POST' || m === 'PATCH' || m === 'DELETE')
    ) {
      if (t) {
        toast.error(
          t('sandboxActionUnavailable') ||
            'Action non disponible en mode Sandbox'
        );
      }
      return {
        errorData: {
          message: 'sandbox_action_unavailable',
          status: 400,
          sandboxBlocked: true,
        },
      };
    }
  }

  if (isCircuitOpen()) {
    return {
      errorData: {
        message: 'Service temporarily unavailable',
        status: 503,
        circuitOpen: true,
      },
    };
  }

  let businessToken = getBusinessToken();
  // Portail public (public.usenode.com) : auth via idToken/token lien, pas de businessToken BOS.
  const isPublicPortalUrl = Boolean(
    (effectiveUrl || url || '').includes('public.usenode.com')
  );
  if (
    !noAuth &&
    !businessToken &&
    !isBusinessTokenOptional(effectiveUrl) &&
    !isPublicPortalUrl
  ) {
    // No token in storage (e.g. fresh cross-domain handoff): try to mint one.
    businessToken = await mintBusinessToken(tokenContext, signal);
    if (!businessToken) {
      return {
        errorData: {
          message: 'business_token_unavailable',
          status: 401,
          businessTokenMissing: true,
        },
      };
    }
  }

  try {
    let idToken = null;
    let response = null;

    if (showLoading) {
      toastId = toast.loading(t('loading'));
    }

    if (!noAuth) {
      const user = auth.currentUser;
      if (user) {
        idToken = await user.getIdToken();
      }
    }

    if (_isAuthRetry && (signal?.aborted || !isCurrentBusinessTokenContext(tokenContext))) {
      throw Object.assign(new Error('auth_context_changed'), { response: { status: 401 } });
    }
    if (expectedUserId && auth.currentUser?.uid !== expectedUserId) {
      throw Object.assign(new Error('ACCOUNT_CHANGED'), { code: 'ACCOUNT_CHANGED' });
    }

    const emulatedRole = localStorage.getItem('emulatedRole');
    const emulatedGroups = localStorage.getItem('emulatedGroups');
    const emulatedUser = localStorage.getItem('emulatedUser');
    const parsedGroups = emulatedGroups ? JSON.parse(emulatedGroups) : [];

    const config = {
      method,
      url: effectiveUrl?.startsWith('http') ? effectiveUrl : `${BASEURL}${effectiveUrl}`,
      data: body,
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'POST,OPTIONS',
        ...(idToken && { Authorization: `Bearer ${idToken}` }),
        ...(!noAuth && businessToken && { businesstoken: businessToken }),
        ...(emulatedRole && { 'x-role-emulated': emulatedRole }),
        ...(parsedGroups.length > 0 && {
          'x-groups-emulated': parsedGroups.join(','),
        }),
        ...(emulatedUser && { 'x-user-id-emulated': emulatedUser }),
        lang,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        'X-Request-Id': generateRequestId(),
        ...getDeviceHeaders(deviceTokenUserForRequest(effectiveUrl, noAuth, expectedUserId || auth.currentUser?.uid)),
        ...headers,
      },
      ...options,
      ...(signal && { signal }),
    };

    let lastError = null;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          const delay = getRetryDelay(lastError, attempt - 1);
          await sleep(delay);
          if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        }
        response = await axios(config);
        recordCircuitSuccess();
        break;
      } catch (err) {
        lastError = err;
        if (
          err.name === 'CanceledError' ||
          err.code === 'ERR_CANCELED' ||
          err.name === 'AbortError'
        ) {
          throw err;
        }
        if (!retry || !shouldRetry(err, attempt)) {
          throw err;
        }
      }
    }

    if (!response && lastError) {
      throw lastError;
    }

    // coreSeqV6 replies 202 + jobId instead of running the mutation inline;
    // wait for the Cloud Tasks job to finish so callers keep getting the same
    // response.data / { errorData } contract they got from coreSeqV5.
    if (response.status === 202 && response?.data?.jobId) {
      const jobResult = await waitForCoreSeqJob(response.data.jobId);

      if (showLoading && toastId !== null) {
        if (jobResult.ok) {
          toast.update(toastId, {
            render: t('success'),
            type: 'success',
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        } else {
          toast.dismiss(toastId);
        }
      }

      if (!jobResult.ok && errorToast) {
        toast.error(t('errorOccurred'));
      }

      if (!jobResult.ok) {
        captureSentryApiFailure({
          method,
          url,
          status: jobResult.errorData?.status,
          // 504 here is the client wait timing out, not a gateway response.
          code: jobResult.errorData?.timeout ? 'coreseq_wait_timeout' : jobResult.errorData?.code,
        });
      }
      return jobResult.ok ? jobResult.data : { errorData: jobResult.errorData };
    }

    if (showLoading && toastId !== null) {
      toast.update(toastId, {
        render: t('success'),
        type: 'success',
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
      });
    }

    return response.data;
  } catch (error) {
    if (showLoading && toastId !== null) {
      toast.dismiss(toastId);
    }

    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      throw error;
    }

    const status = error?.response?.status;
    if (!noAuth && url !== 'users/session') notifySessionExpired(error, tokenContext);
    const deviceRateLimited = status === 429 && error?.response?.data?.code === 'DEVICE_RATE_LIMITED';
    if (status && RETRYABLE_STATUSES.has(status) && !deviceRateLimited) {
      recordCircuitFailure();
    }

    // This expiry is rejected before business work; recover once even when
    // paid requests disable generic retries. Never replay in another context.
    if (!noAuth && !_isAuthRetry && isBusinessTokenExpired(error)) {
      const newToken = await mintBusinessToken(tokenContext, signal);
      if (newToken && !signal?.aborted && isCurrentBusinessTokenContext(tokenContext)) {
        return nodeAxiosFirebase({
          method,
          url,
          body,
          noAuth,
          errorToast,
          showLoading,
          headers,
          options,
          t,
          lang,
          signal,
          retry,
          _isAuthRetry: true,
          expectedUserId,
        });
      }
    }

    if (errorToast) {
      toast.error(t('errorOccurred'));
    }
    const deviceFailure = error?.response?.data;
    if (!String(url).includes('device-security') &&
      (deviceFailure?.code === 'DEVICE_VERIFICATION_REQUIRED' || /^USGEDV/.test(deviceFailure?.intCode || ''))) {
      window.dispatchEvent(new Event('node:device-security-required'));
    }

    if (currentPath?.startsWith('/bos')) {
      const intCode = error?.response?.data?.intCode;
      if (intCode?.endsWith('GEUA003')) {
        redirectSigninFirebase('api-geua003', intCode);
      } else if (intCode?.endsWith('IP9')) {
        toast.error(t('ipRestrictionError'));
        setTimeout(() => {
          redirectSigninFirebase('api-ip-restriction', intCode);
        }, 3000);
      }
    }

    captureSentryApiFailure({ method, url, status, code: error?.response?.data?.intCode });

    const errorData = {
      message: error?.message || 'An error occurred',
      status: error?.response?.status,
      data: error?.response?.data,
      details: error?.response?.data?.details,
      code: error?.code,
    };

    return { errorData };
  }
};

/** nodeAxiosFirebase resolves HTTP errors as `{ errorData }` instead of throwing. */
export function throwIfErrorData(response) {
  if (!response?.errorData) return response;
  const payload = response.errorData;
  const localized =
    payload.data?.message?.fr ||
    payload.data?.message?.en ||
    payload.message ||
    'request_failed';
  const err = new Error(
    typeof localized === 'string' ? localized : 'request_failed'
  );
  err.status = payload.status;
  err.intCode = payload.data?.intCode;
  err.errorData = payload;
  throw err;
}

export default nodeAxiosFirebase;
